/**
 * Project create and edit — the part Or asked about specifically.
 *
 * ⛔ THE KEY IS CREATE-ONLY, and the edit form has no field for it. A project key is embedded in
 *   every ticket reference ever written down (`PH-14`), pasted into a merge request, or sent in a
 *   message. Renaming it silently invalidates all of them, and nothing in the system would report
 *   the breakage — the old references simply stop resolving. That is a migration, not a form field,
 *   and the API refuses it independently.
 */
import { useState } from 'react';
import { createProject, updateProject } from '../../api/endpoints';
import { toApiError } from '../../api/errors';
import type { Project } from '../../api/types';
import { useProjects } from '../../projects/ProjectsProvider';
import { EmptyState } from '../../components/ui/EmptyState';
import { Notice } from '../../components/ui/Notice';
import { Spinner } from '../../components/ui/Skeleton';

const FIELD =
  'h-11 w-full rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-3 text-base outline-none focus:border-[color:var(--color-accent)]';

/** Mirrors the API's `project_key_shape` CHECK, so the refusal is instant rather than a round trip. */
const KEY_PATTERN = /^[A-Z][A-Z0-9]{1,9}$/;

export function AdminProjectsPanel() {
  // ⭐ The SAME provider the project rail reads. A separate fetch here would let the admin screen and
  //   the sidebar disagree about which projects exist right after a create — and `reload()` refreshes
  //   both, so the new project appears in the rail without a page reload.
  const { data, reload } = useProjects();
  const projects = data ?? [];
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Project | null>(null);

  return (
    <div className="space-y-4">
      {error ? <Notice tone="error" title={error} /> : null}

      <CreateProjectForm
        onError={setError}
        onCreated={() => {
          setError(null);
          reload();
        }}
      />

      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Existing projects</h3>

        {projects.length === 0 ? (
          <EmptyState title="No projects yet" detail="Create the first one above." />
        ) : (
          <ul className="space-y-2">
            {projects.map((project) => (
              <li
                key={project.id}
                className="rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-3"
              >
                {editing?.id === project.id ? (
                  <EditProjectForm
                    project={project}
                    onCancel={() => setEditing(null)}
                    onError={setError}
                    onSaved={() => {
                      setEditing(null);
                      setError(null);
                      reload();
                    }}
                  />
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">
                        <span className="mr-2 rounded bg-[color:var(--color-accent)]/15 px-1.5 py-0.5 font-mono text-xs">
                          {project.key}
                        </span>
                        {project.name}
                      </p>
                      <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
                        {project.kind ?? 'software'}
                      </p>
                    </div>
                    <button
                      className="h-9 rounded-md border border-[color:var(--color-line)] px-3 text-xs font-medium hover:bg-[color:var(--color-bg)]"
                      onClick={() => setEditing(project)}
                    >
                      Edit
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function CreateProjectForm({
  onCreated,
  onError,
}: {
  onCreated: () => void;
  onError: (message: string | null) => void;
}) {
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'software' | 'research'>('software');
  const [busy, setBusy] = useState(false);

  const keyValid = KEY_PATTERN.test(key);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    onError(null);
    try {
      await createProject({ key, name: name.trim(), kind });
      setKey('');
      setName('');
      onCreated();
    } catch (err) {
      onError(toApiError(err, '/projects').message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-3 rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-3"
    >
      <div>
        <p className="text-sm font-semibold">New project</p>
        <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
          The key prefixes every ticket in the project and cannot be changed afterwards — choose it
          carefully.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="space-y-1">
          <span className="block text-xs font-medium text-[color:var(--color-ink-muted)]">Key</span>
          <input
            className={`${FIELD} font-mono uppercase`}
            value={key}
            required
            maxLength={10}
            /* ⭐ Upper-cased as they type rather than rejected afterwards: the constraint is
               `^[A-Z][A-Z0-9]{1,9}$`, and someone typing `ph` has made no mistake worth a red box. */
            onChange={(e) => setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
          />
          {key && !keyValid ? (
            <span className="block text-xs text-amber-700 dark:text-amber-300">
              2–10 characters, starting with a letter.
            </span>
          ) : null}
        </label>

        <label className="space-y-1">
          <span className="block text-xs font-medium text-[color:var(--color-ink-muted)]">Name</span>
          <input className={FIELD} value={name} required onChange={(e) => setName(e.target.value)} />
        </label>

        <label className="space-y-1">
          <span className="block text-xs font-medium text-[color:var(--color-ink-muted)]">Kind</span>
          {/* ⚠️ Kind decides which status vocabulary the project's tickets use (db/008 moved the
              entry state onto the project). Not cosmetic. */}
          <select
            className={FIELD}
            value={kind}
            onChange={(e) => setKind(e.target.value as 'software' | 'research')}
          >
            <option value="software">Software</option>
            <option value="research">Research</option>
          </select>
        </label>
      </div>

      <button
        type="submit"
        className="flex h-11 items-center justify-center gap-2 rounded-md bg-[color:var(--color-accent)] px-4 text-sm font-semibold text-[color:var(--color-on-accent)] hover:brightness-110 disabled:pointer-events-none disabled:opacity-50"
        disabled={busy || !keyValid || !name.trim()}
      >
        {busy ? <Spinner label="Creating" /> : null}
        Create project
      </button>
    </form>
  );
}

function EditProjectForm({
  project,
  onSaved,
  onCancel,
  onError,
}: {
  project: Project;
  onSaved: () => void;
  onCancel: () => void;
  onError: (message: string | null) => void;
}) {
  const [name, setName] = useState(project.name);
  const [kind, setKind] = useState<'software' | 'research'>(
    project.kind === 'research' ? 'research' : 'software',
  );
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    onError(null);
    try {
      await updateProject(project.id, { name: name.trim(), kind });
      onSaved();
    } catch (err) {
      onError(toApiError(err, `/projects/${project.id}`).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1">
          <span className="block text-xs font-medium text-[color:var(--color-ink-muted)]">Name</span>
          <input className={FIELD} value={name} required onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="space-y-1">
          <span className="block text-xs font-medium text-[color:var(--color-ink-muted)]">Kind</span>
          <select
            className={FIELD}
            value={kind}
            onChange={(e) => setKind(e.target.value as 'software' | 'research')}
          >
            <option value="software">Software</option>
            <option value="research">Research</option>
          </select>
        </label>
      </div>

      {/* ⛔ The key is shown as text with the reason, rather than as a disabled input. A greyed-out
          field invites "how do I enable this?"; a sentence answers it. */}
      <p className="text-xs text-[color:var(--color-ink-muted)]">
        Key <code className="font-mono">{project.key}</code> cannot be changed — it appears in every
        ticket reference already written down.
      </p>

      <div className="flex gap-2">
        <button
          type="submit"
          className="h-10 rounded-md bg-[color:var(--color-accent)] px-4 text-sm font-semibold text-[color:var(--color-on-accent)] disabled:opacity-50"
          disabled={busy || !name.trim()}
        >
          Save
        </button>
        <button
          type="button"
          className="h-10 rounded-md border border-[color:var(--color-line)] px-4 text-sm"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
